// /loans/closed (D52): the closed-loans report. One row per fully repaid RECORDED borrow with its linked repays
// (expandable), the interest paid and the realized result; totals over the whole filtered set; and, apart from the
// rows, the repays marked as pre-history (D51), which paid back loans MoneyFlow never recorded. The filter lives in
// the URL (deep-linkable); the period is UTC dates, inclusive.
import { useCallback, useMemo, useState } from 'react';
import { getClosedLoans, getLoanList, getLocationItems } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { ClosedAssetTotal, ClosedLoanLine, ClosedLoansView, ClosedPrehistoryLine, ClosedRepayLine, ClosedUsdTotal } from '../api/model';
import { Async, ErrorNotice, Notice } from '../components/Async';
import { CardHead } from '../components/Card';
import { HelpLabel } from '../components/Help';
import { PriceSourceLabel } from '../components/PriceSource';
import { Table, type Column } from '../components/Table';
import { Dash, Val, signClass } from '../components/Val';
import { addDecimal, formatDecimal } from '../lib/decimal';
import {
  PAGE_SIZE,
  PRESETS,
  PRESET_LABEL,
  activePreset,
  closedFilterToParams,
  closedFilterToQuery,
  paramsToClosedFilter,
  presetRange,
  type ClosedFilter,
} from '../lib/closedFilter';
import { Link, useRouter } from '../lib/router';
import { formatUtc } from '../lib/time';

const EMPTY: ClosedLoansView = {
  items: [], total: 0, limit: PAGE_SIZE, offset: 0, prehistory: [],
  totals: { count: 0, per_asset: [], usd: { realized_result_usd: '0', realized_result_reason: null, realized_result_incl_interest_usd: '0', realized_result_incl_interest_reason: null, interest_paid_usd: '0', interest_paid_reason: null } },
};

const resultClass = (v: string | null): string => (signClass(v) === 'neg' ? 'warn' : signClass(v));

/** A USD result: sign-coloured number, or a dash with the server's reason (never 0). */
function Result({ value, reason, testId }: { value: string | null; reason: string | null; testId?: string }) {
  return value === null ? <Dash reason={reason} /> : <span className={`mono ${resultClass(value)}`} data-testid={testId}>{formatDecimal(value, { dp: 2, sign: true })}</span>;
}

function repayColumns(asset: string): Column<ClosedRepayLine>[] {
  return [
    { key: 'at', label: 'Visszafizetve (UTC)', sortValue: (r) => r.occurred_at, render: (r) => <span className="mono">{formatUtc(r.occurred_at)}</span> },
    { key: 'qty', label: 'Hozzárendelt mennyiség', numeric: true, help: 'closedRepayQuantity', ariaLabel: 'hozzárendelt mennyiség', sortValue: (r) => r.quantity, render: (r) => <span className="mono">{formatDecimal(r.quantity)} {asset}</span> },
    { key: 'int', label: 'Kamat-rész', numeric: true, help: 'closedRepayInterest', ariaLabel: 'kamat-rész', sortValue: (r) => r.interest_share, render: (r) => <span className="mono">{formatDecimal(r.interest_share)} {asset}</span> },
    { key: 'lvl', label: 'Ár a visszafizetéskor', numeric: true, help: 'closedRepayPrice', ariaLabel: 'ár a visszafizetéskor', sortValue: (r) => r.repay_price_level, render: (r) => <span className="mono"><Val value={r.repay_price_level} reason={r.realized_result_reason} dp={2} /></span> },
    { key: 'res', label: 'Eredmény, USD', numeric: true, help: 'realizedResult', ariaLabel: 'eredmény', sortValue: (r) => r.realized_result_usd, render: (r) => <Result value={r.realized_result_usd} reason={r.realized_result_reason} /> },
  ];
}

function loanColumns(open: ReadonlySet<string>, toggle: (id: string) => void): Column<ClosedLoanLine>[] {
  return [
    {
      key: 'exp', label: 'Visszafizetések', srOnly: true,
      render: (l) => (
        <button
          type="button" className="btn small expander" aria-expanded={open.has(l.event_id)} data-testid={`closed-expand-${l.event_id}`}
          aria-label={`Visszafizetések: ${formatUtc(l.closed_at)} UTC ${formatDecimal(l.quantity)} ${l.asset}`} onClick={() => toggle(l.event_id)}
        >
          {open.has(l.event_id) ? '▾' : '▸'}
        </button>
      ),
    },
    { key: 'opened', label: 'Felvéve (UTC)', help: 'openedAt', ariaLabel: 'felvétel ideje', sortValue: (l) => l.occurred_at, render: (l) => <span className="mono">{formatUtc(l.occurred_at)}</span> },
    { key: 'closed', label: 'Lezárva (UTC)', help: 'closedAt', ariaLabel: 'lezárás ideje', sortValue: (l) => l.closed_at, render: (l) => <span className="mono" data-testid="closed-at">{formatUtc(l.closed_at)}</span> },
    { key: 'days', label: 'Időtartam (nap)', numeric: true, help: 'loanDuration', ariaLabel: 'időtartam napokban', sortValue: (l) => l.duration_days, render: (l) => <span className="mono">{formatDecimal(l.duration_days, { dp: 1 })}</span> },
    { key: 'loc', label: 'Helyszín', sortValue: (l) => l.location, render: (l) => l.location },
    { key: 'asset', label: 'Coin', sortValue: (l) => l.asset, render: (l) => <b>{l.asset}</b> },
    { key: 'qty', label: 'Felvett', numeric: true, help: 'quantity', ariaLabel: 'felvett mennyiség', sortValue: (l) => l.quantity, render: (l) => <span className="mono">{formatDecimal(l.quantity)} {l.asset}</span> },
    {
      key: 'level', label: 'Felvételi ár', numeric: true, help: 'borrowPrice', ariaLabel: 'ár a felvételkor', sortValue: (l) => l.price_level,
      render: (l) => (
        <span className="mono">
          <Val value={l.price_level} reason={l.price_level_reason} dp={2} />
          {l.price_level !== null && l.price_level_asset && l.price_level_asset !== l.asset ? <span className="muted"> {l.price_level_asset} (ref.)</span> : null}
          {l.price_level !== null ? <PriceSourceLabel source={l.price_level_source} /> : null}
        </span>
      ),
    },
    {
      key: 'interest', label: 'Fizetett kamat', numeric: true, help: 'interestPaid', ariaLabel: 'fizetett kamat', sortValue: (l) => l.interest_paid_quantity,
      render: (l) => (
        <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }} data-testid={`closed-interest-${l.event_id}`}>
          <span className="mono">{formatDecimal(l.interest_paid_quantity)} {l.asset}</span>
          {l.interest_paid_usd === null ? <span className="small"><Dash reason={l.interest_paid_reason} /></span> : <span className="muted small mono">≈ {formatDecimal(l.interest_paid_usd, { dp: 2 })} USD</span>}
        </span>
      ),
    },
    {
      key: 'realized', label: 'Realizált eredmény, USD', numeric: true, help: 'realizedResult', ariaLabel: 'realizált eredmény kamattal', sortValue: (l) => l.realized_result_incl_interest_usd,
      render: (l) => (
        <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }} data-testid={`closed-realized-${l.event_id}`}>
          <Result value={l.realized_result_incl_interest_usd} reason={l.realized_result_incl_interest_reason ?? l.realized_result_reason} />
          {l.realized_result_usd !== null && l.realized_result_incl_interest_usd !== null ? <span className="muted small mono">kamat nélkül: {formatDecimal(l.realized_result_usd, { dp: 2, sign: true })}</span> : null}
        </span>
      ),
    },
    { key: 'deal', label: 'Ügylet', help: 'deal', ariaLabel: 'ügylet', sortValue: (l) => l.deal_name, render: (l) => (l.deal_id === null ? <span className="muted">—</span> : <Link to={`/deals/${l.deal_id}`}>#{l.deal_id} {l.deal_name}</Link>) },
  ];
}

function LoanDetail({ l }: { l: ClosedLoanLine }) {
  return (
    <div className="stack detail-host" style={{ gap: 8 }} data-testid={`closed-detail-${l.event_id}`}>
      <div className="muted small">
        Hozzárendelt visszafizetések · visszafizetett tőke: <span className="mono">{formatDecimal(l.principal_repaid_quantity)} {l.asset}</span>
      </div>
      <div className="subtable">
        <Table columns={repayColumns(l.asset)} rows={l.linked_repays} rowKey={(r) => r.repay_event_id} dense defaultSort={{ key: 'at', dir: 'asc' }} label={`Hozzárendelt visszafizetések: ${formatUtc(l.closed_at)} UTC ${l.asset}`} empty="Nincs hozzárendelt visszafizetés." />
      </div>
    </div>
  );
}

function totalsColumns(): Column<ClosedAssetTotal>[] {
  const usd = (pick: (a: ClosedAssetTotal) => { v: string | null; r: string | null }) => (a: ClosedAssetTotal) => {
    const { v, r } = pick(a);
    return v === null ? <Dash reason={r} /> : <span className="mono">{formatDecimal(v, { dp: 2 })}</span>;
  };
  return [
    { key: 'asset', label: 'Coin', sortValue: (a) => a.asset, render: (a) => <b>{a.asset}</b> },
    { key: 'count', label: 'Lezárt hitel (db)', numeric: true, sortValue: (a) => a.count, render: (a) => <span className="mono">{a.count}</span> },
    { key: 'qty', label: 'Felvett', numeric: true, help: 'quantity', ariaLabel: 'felvett mennyiség', sortValue: (a) => a.quantity, render: (a) => <span className="mono">{formatDecimal(a.quantity)} {a.asset}</span> },
    { key: 'principal', label: 'Visszafizetett tőke', numeric: true, help: 'closedPrincipal', ariaLabel: 'visszafizetett tőke', sortValue: (a) => a.principal_repaid_quantity, render: (a) => <span className="mono">{formatDecimal(a.principal_repaid_quantity)} {a.asset}</span> },
    { key: 'interest', label: 'Fizetett kamat', numeric: true, help: 'interestPaid', ariaLabel: 'fizetett kamat', sortValue: (a) => a.interest_paid_quantity, render: (a) => <span className="mono">{formatDecimal(a.interest_paid_quantity)} {a.asset}</span> },
    { key: 'interest_usd', label: 'Fizetett kamat, USD', numeric: true, help: 'interestPaid', ariaLabel: 'fizetett kamat USD-ben', sortValue: (a) => a.interest_paid_usd, render: usd((a) => ({ v: a.interest_paid_usd, r: a.interest_paid_reason })) },
    {
      key: 'res', label: 'Realizált, kamat nélkül, USD', numeric: true, help: 'realizedResult', ariaLabel: 'realizált eredmény kamat nélkül', sortValue: (a) => a.realized_result_usd,
      render: (a) => <Result value={a.realized_result_usd} reason={a.realized_result_reason} />,
    },
    {
      key: 'res_incl', label: 'Realizált, kamattal, USD', numeric: true, help: 'realizedResult', ariaLabel: 'realizált eredmény kamattal', sortValue: (a) => a.realized_result_incl_interest_usd,
      render: (a) => <Result value={a.realized_result_incl_interest_usd} reason={a.realized_result_incl_interest_reason} />,
    },
  ];
}

function UsdTotalsFooter({ count, usd }: { count: number; usd: ClosedUsdTotal }) {
  return (
    <tr data-testid="closed-usd-total">
      <td>Összesen (USD)</td>
      <td className="num mono">{count}</td>
      <td colSpan={3} />
      <td className="num mono" data-testid="closed-total-interest-usd">{usd.interest_paid_usd === null ? <Dash reason={usd.interest_paid_reason} /> : formatDecimal(usd.interest_paid_usd, { dp: 2 })}</td>
      <td className="num" data-testid="closed-total-result"><Result value={usd.realized_result_usd} reason={usd.realized_result_reason} /></td>
      <td className="num" data-testid="closed-total-result-incl"><Result value={usd.realized_result_incl_interest_usd} reason={usd.realized_result_incl_interest_reason} /></td>
    </tr>
  );
}

function prehistoryColumns(): Column<ClosedPrehistoryLine>[] {
  return [
    { key: 'loc', label: 'Helyszín', sortValue: (g) => g.location, render: (g) => g.location },
    { key: 'asset', label: 'Coin', sortValue: (g) => g.asset, render: (g) => <b>{g.asset}</b> },
    { key: 'count', label: 'Visszafizetés (db)', numeric: true, sortValue: (g) => g.count, render: (g) => <span className="mono">{g.count}</span> },
    { key: 'qty', label: 'Előzmény-tőke', numeric: true, help: 'prehistoryPart', ariaLabel: 'előzmény-tőke', sortValue: (g) => g.prehistory_quantity, render: (g) => <span className="mono">{formatDecimal(g.prehistory_quantity)} {g.asset}</span> },
    { key: 'int', label: 'Ebből kamat', numeric: true, sortValue: (g) => g.interest_quantity, render: (g) => <span className="mono">{formatDecimal(g.interest_quantity)} {g.asset}</span> },
  ];
}

/** One total per coin: quantities of different coins are never added together. */
function PrehistoryFooter({ groups }: { groups: ClosedPrehistoryLine[] }) {
  const byAsset = new Map<string, { count: number; qty: string; interest: string }>();
  for (const g of groups) {
    const t = byAsset.get(g.asset) ?? { count: 0, qty: '0', interest: '0' };
    byAsset.set(g.asset, { count: t.count + g.count, qty: addDecimal(t.qty, g.prehistory_quantity), interest: addDecimal(t.interest, g.interest_quantity) });
  }
  return (
    <>
      {[...byAsset.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([asset, t]) => (
        <tr key={asset} data-testid={`prehistory-total-${asset}`}>
          <td colSpan={2}>Összesen · {asset}</td>
          <td className="num mono">{t.count}</td>
          <td className="num mono">{formatDecimal(t.qty)} {asset}</td>
          <td className="num mono">{formatDecimal(t.interest)} {asset}</td>
        </tr>
      ))}
    </>
  );
}

export function ClosedLoansPage() {
  const { search, navigate } = useRouter();
  const filter = useMemo(() => paramsToClosedFilter(new URLSearchParams(search)), [search]);
  const built = useMemo(() => closedFilterToQuery(filter), [filter]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const closed = useApi((s) => (built.periodError ? Promise.resolve(EMPTY) : getClosedLoans(built.query, s)), [JSON.stringify(built.query), built.periodError]);
  // Filter choices come from the unfiltered loan list, so choosing one never hides the other options.
  const everything = useApi((s) => getLoanList({ status: 'all' }, s));
  const locs = useApi(getLocationItems);

  const assets = useMemo(() => [...new Set((everything.data?.items ?? []).map((l) => l.asset))].sort(), [everything.data]);
  const locations = useMemo(() => {
    const used = new Set((everything.data?.items ?? []).map((l) => l.location_id));
    return (locs.data ?? []).filter((l) => used.has(l.id));
  }, [everything.data, locs.data]);

  const change = useCallback(
    (patch: Partial<ClosedFilter>) => {
      const next = { ...filter, ...patch, page: patch.page ?? 1 };
      const qs = closedFilterToParams(next).toString();
      navigate(`/loans/closed${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [filter, navigate],
  );
  const toggle = useCallback((id: string) => setOpen((o) => { const n = new Set(o); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const cols = useMemo(() => loanColumns(open, toggle), [open, toggle]);
  const preset = activePreset(filter, new Date());
  const isDefault = filter.from === '' && filter.to === '' && filter.locationId === '' && filter.asset === '';

  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Lezárt hitelek</h1>
          <div className="muted">Teljesen visszafizetett, rögzített hitelek részletesen: hozzárendelt visszafizetések, fizetett kamat és realizált eredmény · USD, minden időpont és időszak-határ UTC</div>
        </div>
        <Link className="btn" to="/loans" data-testid="loans-link">← Hitelek</Link>
      </div>

      <section className="card" aria-label="Szűrők" style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px 24px' }}>
        <div className="field">
          <HelpLabel helpKey="closedPeriod" label="időszak" className="flabel">Időszak (UTC)</HelpLabel>
          <div className="seg big" role="group" aria-label="Időszak">
            {PRESETS.map((p) => (
              <button key={p} type="button" className={preset === p ? 'on' : ''} aria-pressed={preset === p} onClick={() => change(presetRange(p, new Date()))}>{PRESET_LABEL[p]}</button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="closed-from">Időszak eleje (UTC, tartalmazza)</label>
          <input id="closed-from" type="date" className="input mono" value={filter.from} onChange={(e) => change({ from: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="closed-to">Időszak vége (UTC, tartalmazza)</label>
          <input id="closed-to" type="date" className="input mono" value={filter.to} onChange={(e) => change({ to: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="closed-loc">Helyszín</label>
          <select id="closed-loc" className="input" value={filter.locationId} onChange={(e) => change({ locationId: e.target.value })}>
            <option value="">Mind</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="closed-asset">Coin</label>
          <select id="closed-asset" className="input" value={filter.asset} onChange={(e) => change({ asset: e.target.value })}>
            <option value="">Mind</option>
            {assets.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button type="button" className="btn" disabled={isDefault} onClick={() => change({ from: '', to: '', locationId: '', asset: '' })}>Szűrők törlése</button>
      </section>

      {built.periodError ? <Notice kind="warn" title="Az időszak nem használható">{built.periodError}</Notice> : null}
      {closed.error ? <ErrorNotice error={closed.error} onRetry={closed.reload} /> : null}

      <Async state={closed}>
        {(data) => {
          const first = data.total === 0 ? 0 : data.offset + 1;
          const last = Math.min(data.offset + data.items.length, data.total);
          const pages = Math.max(1, Math.ceil(data.total / data.limit));
          return (
            <>
              {data.total > 0 ? (
                <section className="card tight" aria-label="Összesítés" data-testid="closed-totals">
                  <CardHead title="Összesítés" hint={<>{data.totals.count} lezárt hitel a szűrésben · coinonként a mennyiségek, alul a USD-összeg; ismeretlen érték helyett „—” és az ok áll, nem nulla</>} />
                  <Table columns={totalsColumns()} rows={data.totals.per_asset} rowKey={(a) => a.asset} dense defaultSort={{ key: 'asset', dir: 'asc' }} label="Összesítés coinonként" footer={<UsdTotalsFooter count={data.totals.count} usd={data.totals.usd} />} />
                </section>
              ) : null}

              <section className="card tight" aria-label="Lezárt hitelek">
                <CardHead title="Lezárt hitelek" hint={<span data-testid="closed-total">{data.total} találat</span>} />
                <Table
                  columns={cols}
                  rows={data.items}
                  rowKey={(l) => l.event_id}
                  dense
                  defaultSort={{ key: 'closed', dir: 'desc' }}
                  expanded={(l) => open.has(l.event_id)}
                  detail={(l) => <LoanDetail l={l} />}
                  label="Lezárt hitelek"
                  empty="Nincs a szűrőnek megfelelő lezárt hitel ebben az időszakban."
                />
                {data.total > data.limit ? (
                  <div className="pager" role="navigation" aria-label="Lapozás">
                    <span data-testid="closed-range">{first}–{last} / {data.total}</span>
                    <span className="muted small">A rendezés az oldalon látható sorokra vonatkozik.</span>
                    <button type="button" className="btn small" disabled={filter.page <= 1} onClick={() => change({ page: filter.page - 1 })}>← Előző</button>
                    <span className="small" data-testid="closed-page">{filter.page}. / {pages}. oldal</span>
                    <button type="button" className="btn small" disabled={filter.page >= pages} onClick={() => change({ page: filter.page + 1 })}>Következő →</button>
                  </div>
                ) : null}
              </section>

              {data.prehistory.length > 0 ? (
                <section className="card tight" aria-label="Rendszeren kívüli régi hitelek törlesztése" data-testid="closed-prehistory">
                  <HelpLabel helpKey="prehistoryReport" label="rendszeren kívüli régi hitelek törlesztése" as="h2" className="">Rendszeren kívüli régi hitelek törlesztése (előzmény)</HelpLabel>
                  <p className="muted small" style={{ margin: 0 }}>
                    Ezek az időszakban megjelölt visszafizetések olyan régi hiteleket törlesztettek, amelyek nincsenek rögzítve a MoneyFlow-ban, ezért nem szerepelnek a lezárt hitelek sorai között.
                  </p>
                  <Table columns={prehistoryColumns()} rows={data.prehistory} rowKey={(g) => `${g.location_id}-${g.asset}`} dense defaultSort={{ key: 'loc', dir: 'asc' }} label="Rendszeren kívüli régi hitelek törlesztése" footer={<PrehistoryFooter groups={data.prehistory} />} />
                </section>
              ) : null}
            </>
          );
        }}
      </Async>
    </main>
  );
}
